import React, { useMemo } from "react";
import Barcode from "react-barcode";
import { Package } from "lucide-react";
import { generateBarcodeDataUrl } from "@/lib/barcode";

/**
 * One MRP sticker at exact physical dimensions.
 *
 * Layout on a 2.5in x 2.5in sticker:
 *
 *   ┌────────────────────────────┬───────────────┐
 *   │ Fields (7 rows)            │ ISI mark      │
 *   │ MRP + (Included of taxes)  │ Product image │
 *   │                            │ + tagline     │
 *   ├────────────────────────────┴───────────────┤
 *   │ Barcode                                    │
 *   ├────────────────────────────────────────────┤
 *   │ Mfd. & Pkd. by:                            │
 *   │ Connect with us:                           │
 *   │ Brand Name:                                │
 *   └────────────────────────────────────────────┘
 */
export const LabelPreview = React.memo(function LabelPreview({
  data,
  sizeIn = 2.5,
  barcodeDataUrl,
}) {
  const p = data || {};
  const ean = (p.ean_code || "").replace(/\s+/g, "");
  const showBarcode = ean.length >= 1;
  const showIsi = p.show_isi !== false && (p.isi_number || p.isi_cml);

  // Always generate a sharp barcode PNG once per EAN. If the parent already
  // pre-computed one (print page tiling many stickers) we reuse that; else we
  // memoize per-instance. This avoids the react-barcode SVG rendering issues.
  const localBarcode = useMemo(
    () => (barcodeDataUrl || !showBarcode ? null : generateBarcodeDataUrl(ean)),
    [barcodeDataUrl, showBarcode, ean]
  );
  const barcodeUrl = barcodeDataUrl || localBarcode;

  const f = (frac) => `${sizeIn * frac}in`;

  return (
    <div
      className="label-sticker relative bg-white text-black flex flex-col"
      style={{
        width: `${sizeIn}in`,
        height: `${sizeIn}in`,
        boxSizing: "border-box",
        border: "1px solid #d4d4d8",
        padding: f(0.018),
        overflow: "hidden",
        fontFamily: "'Merriweather', Georgia, serif",
        fontSize: `${sizeIn * 2.55}pt`,
        lineHeight: 1.07,
      }}
    >
      {/* MAIN ROW: fields (left) + ISI/image (right) */}
      <div className="flex" style={{ flexShrink: 0, gap: f(0.02) }}>
        {/* LEFT COLUMN: fields */}
        <div className="flex-1 min-w-0">
          <Row k="Product Name" v={p.product_name} bold f={f} />
          <Row k="Material Content" v={p.material_content} f={f} />
          <Row k="Net Quantity" v={p.net_quantity} f={f} />
          <Row k="Actual Capacity" v={p.actual_capacity} f={f} />
          <Row k="Country of Origin" v={p.country_of_origin} f={f} />
          <Row k="PKD (Month/Year)" v={p.pkd_month_year} f={f} />
          <Row k="Batch No" v={p.batch_no} f={f} noColon />
          {p.label_type === "copper_customize" && p.mrp && p.mop ? (
            <>
              {/* MRP row — struck through in red */}
              <div style={{ display: "flex", alignItems: "flex-start", marginTop: f(0.003) }}>
                <div style={{ color: "#7f1d1d", minWidth: f(0.36), flexShrink: 0 }}>
                  MRP
                  {p.included_of_taxes !== false && (
                    <div style={{ fontSize: "0.5em", color: "#7f1d1d", lineHeight: 1 }}>
                      (Inclusive of all taxes)
                    </div>
                  )}
                </div>
                <div style={{ color: "#7f1d1d", marginRight: f(0.008) }}>:</div>
                <div
                  style={{
                    color: "#dc2626",
                    textDecoration: "line-through",
                    textDecorationColor: "#dc2626",
                    textDecorationThickness: "0.05em",
                    fontWeight: 400,
                  }}
                  data-testid="copper-customize-mrp-strike"
                >
                  ₹{p.mrp}
                </div>
              </div>
              {/* MOP row — final payable price */}
              <div style={{ display: "flex", alignItems: "flex-start", marginTop: f(0.003) }}>
                <div style={{ color: "#7f1d1d", minWidth: f(0.36), flexShrink: 0 }}>
                  MOP
                  {p.included_of_taxes !== false && (
                    <div style={{ fontSize: "0.5em", color: "#7f1d1d", lineHeight: 1 }}>
                      (Inclusive of all taxes)
                    </div>
                  )}
                </div>
                <div style={{ color: "#7f1d1d", marginRight: f(0.008) }}>:</div>
                <div style={{ color: "#000", fontWeight: 700 }} data-testid="copper-customize-mop">
                  ₹{p.mop}
                </div>
              </div>
            </>
          ) : (
            <div style={{ display: "flex", alignItems: "flex-start", marginTop: f(0.003) }}>
              <div style={{ color: "#7f1d1d", minWidth: f(0.36), flexShrink: 0 }}>
                MRP
                {p.included_of_taxes !== false && (
                  <div style={{ fontSize: "0.5em", color: "#7f1d1d", lineHeight: 1 }}>
                    (Inclusive of all taxes)
                  </div>
                )}
              </div>
              <div style={{ color: "#7f1d1d", marginRight: f(0.008) }}>:</div>
              <div style={{ color: "#000", fontWeight: 400 }}>
                {p.mrp ? `₹${p.mrp}` : ""}
              </div>
            </div>
          )}

          {/* BARCODE — right after MRP inside the left column, no gap */}
          <div style={{ marginTop: f(0.008) }}>
            {showBarcode && barcodeUrl ? (
              <img
                src={barcodeUrl}
                alt={ean}
                style={{
                  display: "block",
                  width: "100%",
                  height: f(0.2),
                  objectFit: "contain",
                  objectPosition: "left center",
                }}
              />
            ) : (
              <div style={{ height: f(0.14), background: "#f4f4f5" }} />
            )}
          </div>
        </div>

        {/* RIGHT COLUMN: ISI mark + product image */}
        <div
          className="flex flex-col items-center flex-shrink-0"
          style={{ width: f(0.36) }}
        >
          {showIsi && (
            <div
              className="flex flex-col items-center"
              style={{ width: "100%", flexShrink: 0 }}
            >
              {p.isi_number && (
                <div
                  style={{
                    fontSize: "0.55em",
                    fontWeight: 700,
                    color: "#000",
                    textAlign: "center",
                    lineHeight: 1,
                  }}
                >
                  {p.isi_number}
                </div>
              )}
              <img
                src="/assets/isi-logo.png"
                alt="ISI"
                style={{
                  width: f(0.2),
                  height: f(0.16),
                  objectFit: "contain",
                  marginTop: f(0.003),
                }}
              />
              {p.isi_cml && (
                <div
                  style={{
                    fontSize: `${Math.max(8, sizeIn * 3.2)}px`,
                    color: "#000",
                    textAlign: "center",
                    lineHeight: 1.1,
                    fontWeight: 500,
                    marginTop: f(0.003),
                  }}
                >
                  {p.isi_cml}
                </div>
              )}
              <div style={{ fontSize: "0.4em", color: "#555", lineHeight: 1 }}>
                www.bis.gov.in
              </div>
            </div>
          )}

          {/* Product image — small enough to leave room for the barcode + address rows */}
          <div
            style={{
              width: "100%",
              marginTop: showIsi ? f(0.008) : 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            {p.product_image ? (
              <img
                src={p.product_image}
                alt=""
                style={{
                  maxWidth: "100%",
                  maxHeight: f(0.36),
                  objectFit: "contain",
                }}
              />
            ) : (
              <div
                className="flex items-center justify-center"
                style={{
                  width: "100%",
                  height: f(0.36),
                  background: "#f4f4f5",
                  color: "#a1a1aa",
                }}
              >
                <Package size={sizeIn * 12} />
              </div>
            )}
            {p.tagline && (
              <div
                style={{
                  fontSize: "0.9em",
                  textAlign: "center",
                  color: "#7f1d1d",
                  marginTop: f(0.005),
                  lineHeight: 1.1,
                  width: "100%",
                }}
              >
                {p.tagline}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ADDRESS blocks — take remaining vertical space with legible, tight text */}
      <div
        style={{
          marginTop: f(0.006),
          fontSize: `${Math.max(7.5, sizeIn * 3.2)}px`,
          lineHeight: 1.1,
          flex: 1,
          minHeight: 0,
          borderTop: "1px solid #e4e4e7",
          paddingTop: f(0.005),
          overflow: "hidden",
        }}
      >
        {p.mfd_pkd_by && (
          <div style={{ marginBottom: f(0.002) }}>
            <span style={{ color: "#7f1d1d" }}>Mfd. &amp; Pkd. by: </span>
            <span style={{ whiteSpace: "pre-line" }}>{p.mfd_pkd_by}</span>
          </div>
        )}
        {p.connect_with_us && (
          <div style={{ marginBottom: f(0.002) }}>
            <span style={{ color: "#7f1d1d" }}>Connect with us: </span>
            <span style={{ whiteSpace: "pre-line" }}>{p.connect_with_us}</span>
          </div>
        )}
        {p.brand_name && (
          <div>
            <span style={{ color: "#7f1d1d" }}>Brand Name: </span>
            <span>{p.brand_name}</span>
          </div>
        )}
      </div>
    </div>
  );
});

const Row = ({ k, v, bold, noColon, f }) => (
  <div style={{ display: "flex", alignItems: "flex-start", marginBottom: f(0.002) }}>
    <div
      style={{
        color: "#7f1d1d",
        fontWeight: bold ? 700 : 400,
        flexShrink: 0,
        minWidth: f(0.36),
      }}
    >
      {k}
    </div>
    <div style={{ color: "#7f1d1d", marginRight: f(0.008) }}>
      {noColon ? "\u00a0" : ":"}
    </div>
    <div
      style={{
        color: "#000",
        fontWeight: bold ? 700 : 400,
        minWidth: 0,
        flex: 1,
        wordBreak: "break-word",
        overflowWrap: "anywhere",
        lineHeight: 1.05,
      }}
    >
      {v || ""}
    </div>
  </div>
);

export function useBarcodeDataUrl(ean) {
  return useMemo(() => generateBarcodeDataUrl(ean), [ean]);
}

export default LabelPreview;
